import CampaignIcon2 from "assets/icons/mono/CampaignIcon2"
import { PRIVILEGE } from "constants/privilege"
import useDocumentTitle from "hooks/useDocumentTitle"
import { useCallback, useEffect, useMemo } from "react"
import Avatar from "react-avatar"
import InfiniteScroll from "react-infinite-scroll-component"
import Skeleton from "react-loading-skeleton"
import { useDispatch, useSelector } from "react-redux"
import { Link, useLocation, useSearchParams } from "react-router-dom"
import { Nav, NavItem, NavLink } from "reactstrap"
import { updateCampaignsCount } from "slices/campaigns/reducer"
import { fetchCampaignActivityThunk, fetchCampaignsCount, fetchCampaignsList } from "slices/campaigns/thunk"
import { setCampaignsLocation } from "slices/ui/reducer"
import { AppDispatch, RootState } from "store"
import { encodeBase64, formatAmount, formatNumber } from "utils/helpers"
import AccessControl from "wrappers/AccessControl"
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { defaultFiltersValue } from "Components/campaigns/collaborations/milestone/Filters"
import IGLogoPNG from "../../assets/images/brands/ig-logo.png"
const Index = () => {

    useDocumentTitle("My Campaigns");

    const dispatch: AppDispatch = useDispatch();

    const location = useLocation();

    const [searchParams, setSearchParams] = useSearchParams();
    const status = searchParams.get('status') ?? 'live';
    const brandId = searchParams.get('brand') ?? 'all';

    const campaignsData = useSelector((state: RootState) => state.campaigns.data[brandId]?.[status]);
    const campaigns = campaignsData?.list ?? [];
    const page = campaignsData?.page ?? 1;
    const has_more = campaignsData?.has_more ?? true;
    const count = campaignsData?.count ?? 0;

    const campaignActivityData = useSelector((state: RootState) => state.campaigns.activity);
    const campaignActivityList = campaignActivityData.list;
    const campaignActivityLoading = campaignActivityData.loading;

    const loading = useSelector((state: RootState) => state.campaigns.loading)
    const error = useSelector((state: RootState) => state.campaigns.error)

    const totals = useSelector((state: RootState) => state.campaigns.totals[brandId]?.data);

    const nextPage = () => {
        if (has_more) {
            const nextPageNumber = page + 1;
            dispatch(fetchCampaignsList({ page: nextPageNumber, status, brand_id: brandId }))
        }
    }


    const fetchCampaigns = useCallback(async (page: number, status: string, force: boolean = false) => {
        dispatch(fetchCampaignsList({ page, status, brand_id: brandId, force }));
    }, [dispatch])

    const fetchCampaignsCounts = useCallback(async () => {
        dispatch(fetchCampaignsCount({ brand_id: brandId }));
    }, [dispatch])

    useEffect(() => {
        fetchCampaigns(1, status);
        fetchCampaignsCounts()
    }, [fetchCampaigns, status])

    const fetchCampaignActivity = async () => {
        dispatch(fetchCampaignActivityThunk({}));
    }

    useEffect(() => {
        fetchCampaignActivity();
    }, [])

    const onTabClick = (tabId: string) => {
        setSearchParams({ status: tabId, ...(brandId !== 'all' ? { brand: brandId } : {}) });
    }

    const getTabId = useMemo(() => {
        const statusParam = searchParams.get('status');
        if (statusParam) return statusParam;
        return 'live';
    }, [searchParams]);

    useEffect(() => {
        if (loading) return;
        dispatch(updateCampaignsCount({ key: status, value: count, brand_id: brandId }))
    }, [status, count, loading])

    useEffect(() => {
        if (location)
            dispatch(setCampaignsLocation(location));
    }, [location])

    const getFiltersQuery = (data: any) => {
        const encodedData = encodeBase64({ ...defaultFiltersValue, ...data });
        return `?filters=${encodedData}`;
    }


    return (
        <div className="campaign-page">
            {/* <OverallTaskSummary /> */}
            <h1 className="campaign-page-title">My Campaigns</h1>
            <div className="d-flex justify-content-between align-items-center campaign-page-info mt-4">
                <Nav tabs className="nav nav-tabs nav-tabs-custom nav-primary nav-justified campaign-nav w-50">
                    <NavItem className='campaign-nav-item'>
                        <NavLink
                            style={{ cursor: "pointer" }}
                            className={`campaign-nav-link ${getTabId === 'live' && 'active-campaign-nav'} text-start`}
                            onClick={() => onTabClick('live')}
                        >
                            Active
                            <span className="count-badge mx-2">{totals?.live ?? 0}</span>
                        </NavLink>
                    </NavItem>
                    {/* <NavItem className='campaign-nav-item'>
                        <NavLink
                            style={{ cursor: "pointer" }}
                            className={`campaign-nav-link ${getTabId === 'completed' && 'active-campaign-nav'} text-start`}
                            onClick={() => onTabClick('completed')}
                        >
                            Completed
                            <span className="count-badge mx-2">{totals?.completed ?? 0}</span>
                        </NavLink>
                    </NavItem> */}
                    <NavItem className='campaign-nav-item'>
                        <NavLink
                            style={{ cursor: "pointer" }}
                            className={`campaign-nav-link ${getTabId === 'pending' && 'active-campaign-nav'} text-start`}
                            onClick={() => onTabClick('pending')}
                        >
                            Pending Approval
                            <span className="count-badge mx-2">{totals?.pending ?? 0}</span>
                        </NavLink>
                    </NavItem>
                    <NavItem className='campaign-nav-item'>
                        <NavLink
                            style={{ cursor: "pointer" }}
                            className={`campaign-nav-link ${getTabId === 'draft' && 'active-campaign-nav'} text-start`}
                            onClick={() => onTabClick('draft')}
                        >
                            Draft
                            <span className="count-badge mx-2">{totals?.draft ?? 0}</span>
                        </NavLink>
                    </NavItem>
                </Nav>
                <div className="d-flex justify-content-end align-items-center">
                    <AccessControl privilege={PRIVILEGE.CAMPAIGN.CAMPAIGN.CREATE}>
                        <Link to={{ pathname: '/campaign/new' }} className="theme-btn theme-btn-md link-as-btn btn-gradient"><CampaignIcon2 className='me-1 fw-bold' size={16} /> Create Campaign</Link>
                    </AccessControl>
                </div>
            </div>
            <div className="campaign-page-content my-3">
                <InfiniteScroll
                    dataLength={campaigns.length}
                    next={nextPage}
                    className='row g-3 overflow-x-hidden'
                    hasMore={has_more}
                    loader={<></>}
                >

                    {
                        campaigns.map((campaign, i) => {
                            const campaignActivity = campaignActivityList.find((campaignActivityItem) => campaignActivityItem.uuid === campaign.uuid)

                            // campaignActivity.
                            const totalPendingTasks = Object.values(campaignActivity?.steps ?? {}).reduce((acc, item) => acc + item.count, 0)

                            return (
                                <div className="campaign-card-outer p-0">
                                    <div key={i} className="campaign-card p-0">
                                        <div className="d-flex justify-content-between align-items-center h-100 flex-fill">
                                            <div className="p-3 h-100 d-flex align-items-center">
                                                <div className="d-flex gap-3">
                                                    <Link to={{ pathname: campaign?.mode === 'offline' ? `/campaign/${campaign?.uuid}/insights` : `/campaign/${campaign.uuid}` }}>
                                                        {
                                                            campaign.brand?.img_url ?
                                                                (
                                                                    <div className="campaign-hero-img bg-image bgc-white" style={{ backgroundImage: `url(${campaign.brand.img_url})` }}>
                                                                    </div>
                                                                ) :
                                                                (
                                                                    <Avatar size={"118px"} round={'6px'} name={campaign.name} maxInitials={1} />
                                                                )
                                                        }
                                                    </Link>

                                                    <div className="d-flex flex-column justify-content-between flex-wrap gap-3">
                                                        <div className="d-flex flex-column gap-1">
                                                            <h6 className="campaign-name m-0">
                                                                {
                                                                    campaign.platform === 'ig' &&
                                                                    <img src={IGLogoPNG} alt="IG Logo" className='linked-posts-ig-logo mt-n1 me-2' />
                                                                }
                                                                {
                                                                    campaign.platform === 'yt' &&
                                                                    <i className='ri-youtube-fill text-danger linked-posts-yt-logo mt-1 me-2'></i>
                                                                }
                                                                {campaign.name}
                                                            </h6>
                                                            <span className="published-at">{campaign?.active_at ?? campaign.created}</span>
                                                            <div className="d-flex align-items-center gap-2">
                                                                {(campaign.mode === 'offline') && <span className="badge language-badge">Track Only</span>}
                                                                {(campaign.status === 'completed') && <span className="badge language-badge">Completed</span>}
                                                            </div>
                                                        </div>
                                                        {
                                                            ['live', 'completed'].includes(status) ?
                                                                (<div className="d-flex campaign-actions gap-2 flex-wrap">
                                                                    <AccessControl privilege={PRIVILEGE.CAMPAIGN.PERFORMANCE.INDEX}>
                                                                        <Link to={`/campaign/${campaign.uuid}/insights`} className="theme-btn theme-btn-md btn-primary-outlined">Insights</Link>
                                                                    </AccessControl>
                                                                    <AccessControl privilege={PRIVILEGE.CAMPAIGN.COLLABORATION.INDEX} ifCondition={campaign.mode !== 'offline'}>
                                                                        <Link to={`/campaign/${campaign.uuid}/collaborations`} className="theme-btn theme-btn-md btn-primary-outlined">View Collaborations</Link>
                                                                    </AccessControl>
                                                                    <AccessControl privilege={PRIVILEGE.CAMPAIGN.APPLICATION.INDEX} ifCondition={campaign.mode !== 'offline'}>
                                                                        <Link to={`/campaign/${campaign.uuid}/applications`} className="theme-btn theme-btn-md btn-gradient">View Applications</Link>
                                                                    </AccessControl>
                                                                </div>)
                                                                :
                                                                (<div className="d-flex campaign-actions gap-2 flex-wrap">
                                                                    <AccessControl privilege={PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT}>
                                                                        <Link to={`/campaign/${campaign.uuid}/edit`} className="theme-btn theme-btn-md btn-primary-outlined">Edit</Link>
                                                                    </AccessControl>
                                                                    <AccessControl privilege={PRIVILEGE.CAMPAIGN.CAMPAIGN.SHOW}>
                                                                        <Link to={`/campaign/${campaign.uuid}`} className="theme-btn theme-btn-md btn-gradient">View</Link>
                                                                    </AccessControl>
                                                                </div>)
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            {
                                                ['live', 'completed'].includes(status) &&
                                                <div className="h-100 w-510px d-none d-md-block">
                                                    {
                                                        campaign.mode === 'offline' ? (
                                                            <div className="d-flex align-items-center justify-content-end" style={{ height: '166px' }}>
                                                                <div className="campaign-stat-item h-100" style={{ width: '140px', borderLeft: '1px solid #EDEBEC' }}>
                                                                    <span className="stat-value">{formatNumber(campaign.views_count)}</span>
                                                                    <span className="stat-label">Views</span>
                                                                </div>
                                                                <div className="campaign-stat-item h-100" style={{ width: '139px', borderLeft: '1px solid #EDEBEC' }}>
                                                                    <span className="stat-value">{formatNumber(campaign.interactions_count)}</span>
                                                                    <span className="stat-label">Interactions</span>
                                                                </div>
                                                                <div className="campaign-stat-item h-100" style={{ width: '129px', borderLeft: '1px solid #EDEBEC' }}>
                                                                    <span className="stat-value">{formatNumber(campaign.collaboration_count)}</span>
                                                                    <span className="stat-label">Collaborations</span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="row campaign-stats h-100 justify-content-end">
                                                                {
                                                                    Number(campaign.version) >= 2 ? (
                                                                        <div className="col-4 campaign-stat-item">
                                                                            <span className={`stat-value`}>{campaign.requirements.creator_count?.max}</span>
                                                                            <span className="stat-label">No. of Creators</span>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="col-4 campaign-stat-item">
                                                                            <span className={`stat-value ${Number(campaign.remaining_budget ?? 0) < 0 ? 'text-danger' : 'text-success'}`}>{campaign.remaining_budget ? formatAmount(campaign.remaining_budget, campaign.currency?.code, campaign.currency?.symbol, campaign.currency?.locale) : '-'}</span>
                                                                            <span className="stat-label">Budget Remaining</span>
                                                                        </div>
                                                                    )
                                                                }

                                                                <div className="col-4 campaign-stat-item">
                                                                    <span className="stat-value">{formatNumber(campaign.views_count)}</span>
                                                                    <span className="stat-label">Views</span>
                                                                </div>
                                                                <div className="col-4 campaign-stat-item">
                                                                    <span className="stat-value">{formatNumber(campaign.interactions_count)}</span>
                                                                    <span className="stat-label">Interactions</span>
                                                                </div>
                                                                <div className="col-4 campaign-stat-item">
                                                                    <span className="stat-value">{formatAmount(campaign.budget, campaign.currency?.code, campaign.currency?.symbol, campaign.currency?.locale)}</span>
                                                                    <span className="stat-label">Creator Budget</span>
                                                                </div>
                                                                <div className="col-4 campaign-stat-item">
                                                                    <span className="stat-value">{formatNumber(campaign.total_application_count)}</span>
                                                                    <span className="stat-label">Applications</span>
                                                                </div>
                                                                <div className="col-4 campaign-stat-item">
                                                                    <span className="stat-value">{formatNumber(campaign.collaboration_count)}</span>
                                                                    <span className="stat-label">Collaborations</span>
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            }
                                        </div>
                                    </div>
                                    {
                                        campaignActivity &&
                                        <div className="d-flex justify-content-between align-items-center flex-column flex-md-row p-3">
                                            <h6 className="campaign-card-pending-task-heading mb-0">
                                                <Link className="unset-all cursor-pointer" target='_blank' to={{ pathname: '/collaboration', search: getFiltersQuery({ status: 'ongoing', campaign_id: campaignActivity.id, campaign: { label: campaignActivity.name, value: campaignActivity.id }, assignee: 'agency' }) }}>
                                                    Pending Tasks
                                                    (
                                                    {totalPendingTasks}
                                                    )
                                                </Link>
                                            </h6>
                                            {
                                                campaignActivity ?
                                                    (<div className="swiper-area">
                                                        <Swiper modules={[Navigation]}
                                                            spaceBetween={10}
                                                            navigation={{
                                                                nextEl: ".swiper-button-next",
                                                                prevEl: ".swiper-button-prev",
                                                            }}
                                                            pagination={true}
                                                            breakpoints={{
                                                                640: {
                                                                    slidesPerView: 2,
                                                                    spaceBetween: 8,
                                                                },
                                                                768: {
                                                                    slidesPerView: 2,
                                                                    spaceBetween: 8,
                                                                },
                                                                1024: {
                                                                    slidesPerView: 3,
                                                                    spaceBetween: 8,
                                                                },
                                                            }}
                                                            className="mySwiper navigation-swiper overflow-y-visible">
                                                            {
                                                                campaignActivityLoading ? (
                                                                    <>
                                                                        {
                                                                            Array.from({ length: 4 }).map((_, i) => <SwiperSlide><Skeleton height={80} className='rounded-4' /></SwiperSlide>)
                                                                        }
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {(Object.entries(campaignActivity?.steps ?? {})).map(([slug, step], index: number) => (
                                                                            <SwiperSlide key={index}>
                                                                                <Link target='_blank' to={{ pathname: '/collaboration', search: getFiltersQuery({ status: 'ongoing', campaign_id: campaignActivity.id, campaign: { label: campaignActivity.name, value: campaignActivity.id }, assignee: 'agency', gig_step: slug, gig_step_obj: { value: slug, label: step.name } }) }}>
                                                                                    <div className="pending-step-card-slide" key={index}>
                                                                                        <h5>{step.name}</h5>
                                                                                        <div className="d-flex align-items-center">
                                                                                            <div className="flex-grow-1">
                                                                                                <h4 className="mb-0 text-primary d-flex justify-content-between align-items-center w-100"><span className="counter-value" data-target={step.count}>
                                                                                                    {step.count}
                                                                                                </span>
                                                                                                </h4>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </Link>
                                                                            </SwiperSlide>
                                                                        ))}

                                                                    </>
                                                                )
                                                            }
                                                        </Swiper>
                                                    </div>) : (
                                                        <div className="d-flex align-items-center justify-content-center me-md-5">
                                                            <h6 className="m-0 me-md-5 pe-md-4">No Pending Tasks</h6>
                                                        </div>
                                                    )
                                            }
                                        </div>
                                    }
                                </div>

                            )
                        })
                    }
                    {
                        loading && Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} height={150} className='w-100' />
                        ))
                    }
                    {
                        !loading && error &&
                        <div className="d-flex justify-content-center align-items-center">
                            <button className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>Load More <i className='ms-2 fs-5 bx bx-refresh'></i></button>
                        </div>
                    }
                </InfiniteScroll>
                {
                    !loading && campaigns.length === 0 &&
                    <div className="d-flex w-100 justify-content-center align-items-center">
                        <div className="length-zero">
                            <h3>Currently there is no campaign in your list</h3>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

export default Index